import { BaseEntity, LocalDate, Timestamp } from '@lemon/core/model/common'

export type SelvityspyynnonTila = 'avoin' | 'vastattu'

/**
 * Päätellään kirjauksesta tallennettaessa (KirjanpitoJaettuService.paatteleSelvityspyynnonTyyppi):
 * 'kuititon' kun selvitettävällä tilillä olevalla kirjauksella ei ole yhtään liitettyä tositetta, muutoin 'lisatietopyynto'.
 * Kirjanpitäjän dialogissa valitsema tyyppi (Selvityspyynto.tyyppiValittu) ohittaa päättelyn.
 */
export type SelvityspyynnonTyyppi = 'kuititon' | 'lisatietopyynto'

export type SelvityspyynnonKirjausrivinPuoli = 'debet' | 'kredit'

export const annaSelvityspyynnonKirjausrivinPuoli = (rivi: { d?: number | null }): SelvityspyynnonKirjausrivinPuoli => (rivi.d !== null && rivi.d !== undefined ? 'debet' : 'kredit')

/** Summa luetaan samalta puolelta kuin kirjausrivinPuoli, jotta nolla debet-summa ei vaihdu kredit-puolen arvoon. */
export const annaSelvityspyynnonSumma = (rivi: { d?: number | null; k?: number | null }): number =>
  annaSelvityspyynnonKirjausrivinPuoli(rivi) === 'debet' ? rivi.d : (rivi.k ?? 0)

export interface KirjausSelvityspyynnossa {
  avain: string
}

export interface KirjanpitajanRaahaamaSelvityspyynnossa {
  avain: string
}

/** avain === Kirjausrivin avain (Kirjausrivi.av) */
export interface Selvityspyynto extends BaseEntity {
  kirjausAvain: string
  asiakasId: string
  asiakasAvain: string

  tila: SelvityspyynnonTila
  /** Maksunsaaja tai maksaja (vrt. tositteen w) */
  maksunsaaja: string
  /** Tilitapahtuman lisätieto (vrt. tositteen c) */
  kirjauksenViesti?: string
  /** Tilitapahtuman viesti ja lisätiedot (vrt. tositteen c) — otsikon fallback, kun maksunsaaja puuttuu */
  viesti?: string
  summa: number
  /** Kummalla puolella selvitettävän tilin rivi on. Kredit on palautus tai hyvitys, debet kulu. Puuttuu ennen 29.9.2026 luoduilta. */
  kirjausrivinPuoli?: SelvityspyynnonKirjausrivinPuoli
  tyyppi: SelvityspyynnonTyyppi
  /** Kirjanpitäjä valitsi tyypin selvityspyyntödialogissa — tallennus ei päättele sitä enää liitteistä */
  tyyppiValittu?: true
  /**
   * Tallennus täyttää tositeAvaimet- ja laskuAvaimet-kentät kirjauksen liitteistä; poistuu, kun kirjanpitäjä tallentaa liitevalinnan selvityspyyntödialogissa.
   * Lipun puuttuminen tarkoittaa jäädytettyä valintaa, koska ennen lippua tallennetuilla selvityspyynnöillä voi olla käsin valitut avaimet ilman merkintää.
   */
  liitteetKirjauksesta?: true
  pvml: LocalDate
  /** Kirjanpitäjän kysymys/viesti asiakkaalle */
  kysymys: string

  /** Asiakkaan vastaus kirjanpitäjälle */
  vastaus?: string
  /** Milloin asiakas vastasi */
  vastattu?: Timestamp
  /** Kuka asiakkaan käyttäjistä vastasi */
  vastaaja?: string
  /** Lähdekirjauksen maksutavan tunniste */
  maksutapa?: number

  /** Lähdetilitapahtuman avain, jos kirjaus on lähtöisin tilitapahtumasta */
  tilitapahtumaAvain?: string
  kirjaus?: KirjausSelvityspyynnossa
  /** Kirjanpitäjän valitsemat, selvityspyyntöön liitettävät tositteet (avaimet) */
  tositeAvaimet?: string[]
  /** Lemonatorin vanhalle sovellukselle luomat kuvattomat paikkatositteet, jotka vastaus kuittaa. Eivät näy asiakkaalle tositteina. */
  paikkatositeAvaimet?: string[]
  /** Asiakkaan vastauksena lisäämät tositteet (avaimet); tositteet linkittyvät kirjaukseen tagTilitapahtumat-kentällä */
  vastausTositeAvaimet?: string[]
  /** Kirjanpitäjän valitsemat, selvityspyyntöön liitettävät laskut (avaimet) */
  laskuAvaimet?: string[]
  raahatut?: KirjanpitajanRaahaamaSelvityspyynnossa[]
  aikaisemmat?: Selvityspyynto[]
  /** Vain Lemonatorin kirjausrivillä: lukitun kuukauden kirjauksen pyynnön vastaus kuitattiin tällä kirjauksella */
  kuittauskirjausAvain?: string
  /** Vain Lemonatorin kirjausrivillä: kuittauskirjauksen pyyntö lähti alun perin tältä kirjaukselta */
  alkuperainenKirjausAvain?: string
}

/** Työjonomerkintä, joka käynnistää vastatun selvityspyynnön siirron omaan collectioniin. Avain === selvityspyynnön (kirjausrivin) avain. */
export interface SelvityspyyntoVastattuTyojono {
  aloitettu: Timestamp
}
